import {useCallback, useState} from 'react';

import {useNotifications} from '@/providers/components/notificationsProvider';

import Icon from '@/components/icon';
import {Pagination} from '@/components/pagination';

import {NotificationItem} from './NotificationItem';
import {SearchBar} from './SearchBar';
import {SortDropdown, SortOptionT} from './SortDropdown';

function ArchivedNotificationList() {
  const {archivedNotifications, loading, archivedPagination, searchQuery, searchResults, unarchiveAllNotifications} =
    useNotifications();
  const [currentSort, setCurrentSort] = useState<SortOptionT>({
    label: 'Newest First',
    value: 'created_at',
    direction: 'desc',
  });

  const handleUnarchiveAll = useCallback(async () => {
    await unarchiveAllNotifications();
  }, [unarchiveAllNotifications]);

  if (loading) {
    return (
      <div className="flex items-center justify-center">
        <Icon name="refresh" animate="spin" size={48} color="primary" />
      </div>
    );
  }

  const displayedNotifications = searchQuery
    ? searchResults.filter(n => n.status === 'archived')
    : archivedNotifications;

  // Sort notifications
  const sortedNotifications = [...displayedNotifications].sort((a, b) => {
    const {value, direction} = currentSort;
    const multiplier = direction === 'asc' ? 1 : -1;

    switch (value) {
      case 'created_at':
        return multiplier * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      case 'title':
        return multiplier * a.title.localeCompare(b.title);
      case 'type':
        return multiplier * a.type.localeCompare(b.type);
      case 'status':
        return multiplier * a.status.localeCompare(b.status);
      default:
        return 0;
    }
  });

  if (sortedNotifications.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <SearchBar />
          <SortDropdown currentSort={currentSort} onSortChange={setCurrentSort} />
        </div>
        <div className="flex h-64 flex-col items-center justify-center space-y-2 text-center">
          <Icon name="archive" size="2xl" color="primary" />
          <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
            {searchQuery ? 'No archived notifications found' : 'No archived notifications'}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {searchQuery ? 'Try a different search term' : 'Notifications you archive will appear here'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex flex-1 items-center gap-4">
          <SearchBar />
          <button
            onClick={handleUnarchiveAll}
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            <Icon name="archive-out" className="h-4 w-4" />
            Unarchive All
          </button>
        </div>
        <SortDropdown currentSort={currentSort} onSortChange={setCurrentSort} />
      </div>
      {sortedNotifications.map(notification => (
        <NotificationItem key={notification.notification_id} notification={notification} />
      ))}
      {!searchQuery && archivedPagination.totalPages > 1 && (
        <div className="mt-4">
          <Pagination
            currentPage={archivedPagination.currentPage}
            totalPages={archivedPagination.totalPages}
            onPageChange={archivedPagination.setPage}
          />
        </div>
      )}
    </div>
  );
}

export default ArchivedNotificationList;
